import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { EventProvider } from "@/context/EventContext";
import { Spinner } from "@/components/Brand";
import PageTitle from "@/components/PageTitle";

import Landing from "@/pages/Landing";
import StaffLogin from "@/pages/StaffLogin";
import VisitorLogin from "@/pages/VisitorLogin";

import VisitorDirectory from "@/pages/visitor/VisitorDirectory";
import ExhibitorDetail from "@/pages/visitor/ExhibitorDetail";
import VisitorScan from "@/pages/visitor/VisitorScan";
import VisitorHistory from "@/pages/visitor/VisitorHistory";
import VisitorSaved from "@/pages/visitor/VisitorSaved";
import VisitorSummary from "@/pages/visitor/VisitorSummary";

import ExhibitorDashboard from "@/pages/exhibitor/ExhibitorDashboard";
import ExhibitorCatalogues from "@/pages/exhibitor/ExhibitorCatalogues";
import ExhibitorLeads from "@/pages/exhibitor/ExhibitorLeads";
import LeadDetail from "@/pages/exhibitor/LeadDetail";
import ExhibitorScan from "@/pages/exhibitor/ExhibitorScan";
import ExhibitorStaff from "@/pages/exhibitor/ExhibitorStaff";
import ExhibitorQR from "@/pages/exhibitor/ExhibitorQR";
import ExhibitorFollowups from "@/pages/exhibitor/ExhibitorFollowups";
import Networking from "@/pages/exhibitor/Networking";
import ExhibitorEnquiries from "@/pages/exhibitor/ExhibitorEnquiries";
import ProductLibrary from "@/pages/exhibitor/ProductLibrary";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import ShareCatalogue from "@/pages/ShareCatalogue";
import AdminLogin from "@/pages/AdminLogin";

function Protected({ roles, children }) {
  const { token, role, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  if (!token) return <Navigate to="/" replace />;
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function Router() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/visitor/login" element={<VisitorLogin />} />
      <Route path="/sv-admin" element={<AdminLogin />} />

      {/* Public catalogue share link */}
      <Route path="/share/catalogue/:id" element={<ShareCatalogue />} />
      <Route path="/share/t/:token" element={<ShareCatalogue />} />

      {/* Visitor */}
      <Route path="/visitor" element={<Protected roles={["visitor"]}><VisitorDirectory /></Protected>} />
      <Route path="/visitor/exhibitor/:id" element={<Protected roles={["visitor"]}><ExhibitorDetail /></Protected>} />
      <Route path="/visitor/scan" element={<Protected roles={["visitor"]}><VisitorScan /></Protected>} />
      <Route path="/visitor/history" element={<Protected roles={["visitor"]}><VisitorHistory /></Protected>} />
      <Route path="/visitor/favourites" element={<Protected roles={["visitor"]}><VisitorSaved /></Protected>} />
      <Route path="/visitor/summary" element={<Protected roles={["visitor"]}><VisitorSummary /></Protected>} />

      {/* Exhibitor */}
      <Route path="/exhibitor" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorDashboard /></Protected>} />
      <Route path="/exhibitor/catalogues" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorCatalogues /></Protected>} />
      <Route path="/exhibitor/leads" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorLeads /></Protected>} />
      <Route path="/exhibitor/leads/:id" element={<Protected roles={["exhibitor", "staff"]}><LeadDetail /></Protected>} />
      <Route path="/exhibitor/scan" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorScan /></Protected>} />
      <Route path="/exhibitor/staff" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorStaff /></Protected>} />
      <Route path="/exhibitor/qr" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorQR /></Protected>} />
      <Route path="/exhibitor/followups" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorFollowups /></Protected>} />
      <Route path="/exhibitor/networking" element={<Protected roles={["exhibitor", "staff"]}><Networking /></Protected>} />
      <Route path="/exhibitor/enquiries" element={<Protected roles={["exhibitor", "staff"]}><ExhibitorEnquiries /></Protected>} />
      <Route path="/exhibitor/products" element={<Protected roles={["exhibitor", "staff"]}><ProductLibrary /></Protected>} />

      {/* Admin */}
      <Route path="/admin" element={<Protected roles={["jjs_admin", "superadmin"]}><AdminDashboard /></Protected>} />
      <Route path="/superadmin" element={<Protected roles={["superadmin"]}><SuperAdmin /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <EventProvider>
            <PageTitle />
            <Router />
          </EventProvider>
        </BrowserRouter>
        <Toaster position="top-center" theme="light" richColors />
      </AuthProvider>
    </div>
  );
}
